import { useId, useState } from 'react';
import { applyFilters, can, MIN_REASON, promoSummary } from '@urbanrats/ops-core';
import { ChangeRequests } from '../components/ChangeRequests.tsx';
import { PageHead, Section } from '../components/common.tsx';
import { fmtAtomic, fmtMoney, fmtOdds, useCtx } from '../lib.ts';
import { Campaigns } from './Campaigns.tsx';

export function Promotions() {
  const { data, filters, user, backend, refresh } = useCtx();
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const id = useId();
  const players = new Set(applyFilters(data, filters).players.map((p) => p.playerRef));
  const sum = promoSummary(data.rewards.filter((r) => players.has(r.playerRef)), data.asOf);
  const on = backend.lightningOn();
  return (
    <>
      <PageHead title="Promotions">{backend.kind === 'api' ? 'Campaigns give freebets and boosts; below, the rewards players hold now (liability = unused freebet face value). Leaderboards and drop chests are on Rewards & leaderboards.' : 'Demo rewards; real-money promotions are off by type. Liability = unused freebet face value. Cost (credits paid) is on the Finance page.'}</PageHead>
      {backend.kind === 'api' && backend.server && <Campaigns />}
      {sum.length === 0 && <p className="empty">No rewards in scope.</p>}
      {sum.map((s) => (
        <Section key={s.currency} title={s.currency} testId={`promo-${s.currency}`}>
          <p>
            {Object.entries(s.counts)
              .map(([k, v]) => `${k} ${v}`)
              .join(' · ')}
          </p>
          <p>
            Outstanding freebet liability: <strong>{fmtAtomic(s.outstandingFreebetFace, s.scale, s.currency)}</strong> · expiring within 24 h: {s.expiringSoon}
          </p>
        </Section>
      ))}
      <Section title="Policies in force" testId="promo-policies">
        {data.promoPolicies.map(({ currency, policy }) => (
          <p key={currency}>
            {currency}: {policy.policyVersion} · cap {fmtMoney(policy.payoutCap)} · max boost +{policy.maxBoostBpsPerBet / 100}% · {policy.maxRewardsPerBet} reward per bet · freebet with Lightning {policy.freebetWithLightning ? 'yes' : 'no'}
          </p>
        ))}
        <p>
          Lightning{' '}
          {data.lightningPolicy ? `${data.lightningPolicy.policyVersion}: ${data.lightningPolicy.tiers.map((t) => `${t.tier} ${t.weight / 100}% ${fmtOdds(t.factor)}`).join(' · ')} · ` : '(no active policy) · '}
          currently <strong data-testid="lightning-state">{on ? 'ON' : 'OFF'}</strong> for new rounds
        </p>
      </Section>
      {user.role !== 'PARTNER_ADMIN' && (
        <Section title="Critical configuration (maker-checker)" testId="config">
          {can(user, 'request.config') ? (
            <form
              className="form inline"
              onSubmit={async (e) => {
                e.preventDefault();
                const r = await backend.request(user, 'CONFIG_CHANGE', 'lightning', `Turn Lightning ${on ? 'OFF' : 'ON'} for new rounds`, { setting: 'LIGHTNING', value: on ? 'OFF' : 'ON' }, reason);
                setMsg(r.ok ? `Request ${r.value.requestId} created — needs a checker (finance or platform admin).` : `${r.error.code}: ${r.error.message}`);
                if (r.ok) setReason('');
                refresh();
              }}
            >
              <label htmlFor={id}>Reason (min {MIN_REASON} chars)</label>
              <input id={id} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="config-reason" />
              <button type="submit" className="btn" data-testid="config-request">
                Request: Lightning {on ? 'OFF' : 'ON'} for new rounds
              </button>
              <p className="note">Applies to rounds opened after approval. Locked manifests and accepted quotes never change.</p>
              {msg && (
                <p role="status" className="status-msg">
                  {msg}
                </p>
              )}
            </form>
          ) : (
            <p className="muted">Your role cannot request configuration changes.</p>
          )}
          <ChangeRequests filter={(r) => r.kind === 'CONFIG_CHANGE'} testId="config-requests" />
        </Section>
      )}
    </>
  );
}
